import {
  Car,
  ChevronDown,
  ChevronUp,
  DollarSign,
  HelpCircle,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  Phone,
  Search,
  Shield,
  User,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import {
  ContactInfo,
  ContactUsService,
} from "@/services/landing/contactUsService";

type FAQItem = {
  answer: string;
  category: string;
  id: number;
  order?: number;
  question: string;
};

const getCategoryIcon = (category: string) => {
  const key = category.toLowerCase();

  if (key.includes("ride")) return <Car size={20} />;
  if (key.includes("payment") || key.includes("price"))
    return <DollarSign size={20} />;
  if (key.includes("safety") || key.includes("security"))
    return <Shield size={20} />;
  if (key.includes("driver")) return <User size={20} />;
  if (key.includes("delivery")) return <Package size={20} />;
  if (key.includes("scooter")) return <Zap size={20} />;
  if (key.includes("account") || key.includes("profile"))
    return <User size={20} />;

  return <HelpCircle size={20} />;
};

export default function FAQ() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openFAQ, setOpenFAQ] = useState<null | number>(null);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadFaqs = async () => {
      try {
        setIsLoading(true);
        setError("");

        const base = String(import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
        const url = `${base}/faqs`;

        console.info("[FAQ] Fetching:", url);

        const response = await fetch(url, {
          method: "GET",
          signal: controller.signal,
        });

        if (!response.ok) {
          const body = await response.text().catch(() => "");
          throw new Error(`Failed to load FAQs (${response.status} ${response.statusText}) ${body}`);
        }

        const result = await response.json();

        const rawFaqs = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result?.faqs)
              ? result.faqs
              : [];

        // Optional debug:
        console.info("[FAQ] API payload:", result);
        console.info("[FAQ] Parsed FAQs:", rawFaqs.length);

        const normalized: FAQItem[] = rawFaqs
          .map((item: any, index: number) => ({
            answer: String(item.answer ?? ""),
            category: String(item.category ?? "General"),
            id: Number(item.id ?? item.faq_id ?? index + 1),
            order:
              item.order !== undefined && item.order !== null
                ? Number(item.order)
                : undefined,
            question: String(item.question ?? ""),
          }))
          .filter((item: FAQItem) => item.question && item.answer)
          .sort((a: FAQItem, b: FAQItem) => (a.order ?? 9999) - (b.order ?? 9999) || a.id - b.id);

        setFaqs(normalized);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        console.error("[FAQ] loadFaqs error:", err);
        setError(err instanceof Error ? err.message : "Unable to fetch FAQs right now.");
      } finally {
        setIsLoading(false);
      }
    };
    const loadContactInfo = async () => {
      try {
        const data = await ContactUsService.get();
        setContactInfo(data);
      } catch (err) {
        console.error("[FAQ] Failed to fetch contact info:", err);
      }
    };

    loadFaqs();
    loadContactInfo();

    return () => controller.abort();
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(
        faqs
          .map((faq) => faq.category)
          .filter((category) => category && category.trim().length > 0),
      ),
    );

    return [
      { icon: <HelpCircle size={20} />, id: "all", label: "All Questions" },
      ...unique.map((category) => ({
        icon: getCategoryIcon(category),
        id: category,
        label: category,
      })),
    ];
  }, [faqs]);

  const filteredFAQs = useMemo(() => {
    return faqs.filter((faq) => {
      const matchesCategory =
        activeCategory === "all" || faq.category === activeCategory;
      const lowerSearch = searchTerm.toLowerCase();
      const matchesSearch =
        faq.question.toLowerCase().includes(lowerSearch) ||
        faq.answer.toLowerCase().includes(lowerSearch);
      return matchesCategory && matchesSearch;
    });
  }, [faqs, activeCategory, searchTerm]);

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const popularQuestions = useMemo(() => faqs.slice(0, 6), [faqs]);

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="bg-primary py-16 text-primary-content">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-primary-content/20">
                <HelpCircle size={48} />
              </div>
              <h1 className="mb-4 text-4xl font-bold md:text-6xl">
                Frequently Asked Questions
              </h1>
              <p className="mx-auto max-w-2xl text-xl opacity-90">
                Find answers to common questions about Hareeng rides,
                deliveries, and services.
              </p>
            </div>
          </div>
        </section>

        {/* Search Section */}
        <section className="bg-base-100 py-12">
          <div className="mx-auto max-w-4xl px-4">
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/50"
                size={20}
              />
              <input
                className="input input-bordered w-full pl-12 pr-4 text-lg"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for answers..."
                type="text"
                value={searchTerm}
              />
            </div>
          </div>
        </section>

        {/* Popular Questions */}
        {!searchTerm && (
          <section className="py-12">
            <div className="mx-auto max-w-7xl px-4">
              <h2 className="mb-8 text-center text-3xl font-bold">
                Popular Questions
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {popularQuestions.map((faq) => (
                  <div
                    className="card cursor-pointer bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
                    key={faq.id}
                    onClick={() => {
                      setActiveCategory(faq.category);
                      setOpenFAQ(faq.id);
                    }}
                  >
                    <div className="card-body">
                      <h3 className="card-title text-lg">{faq.question}</h3>
                      <p className="line-clamp-2 text-sm text-base-content/70">
                        {faq.answer}
                      </p>
                      <div className="card-actions justify-end">
                        <span className="text-sm text-primary">
                          Read more →
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Categories */}
        <section className="bg-base-100 py-8">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <button
                  className={`btn btn-sm ${
                    activeCategory === category.id
                      ? "btn-primary"
                      : "btn-outline"
                  }`}
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.icon}
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4">
            {isLoading ? (
              <div className="py-12 text-center">
                <p className="text-base-content/70">Loading FAQs...</p>
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <h3 className="mb-2 text-xl font-semibold">Error</h3>
                <p className="text-base-content/70">{error}</p>
              </div>
            ) : filteredFAQs.length === 0 ? (
              <div className="py-12 text-center">
                <HelpCircle
                  className="mx-auto mb-4 text-base-content/50"
                  size={48}
                />
                <h3 className="mb-2 text-xl font-semibold">
                  No questions found
                </h3>
                <p className="text-base-content/70">
                  Try adjusting your search terms or browse different
                  categories.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFAQs.map((faq) => (
                  <div className="card bg-base-100 shadow-md" key={faq.id}>
                    <div
                      className="card-body cursor-pointer transition-colors hover:bg-base-200/50"
                      onClick={() => toggleFAQ(faq.id)}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="card-title pr-4 text-lg">
                          {faq.question}
                        </h3>
                        {openFAQ === faq.id ? (
                          <ChevronUp
                            className="flex-shrink-0 text-primary"
                            size={20}
                          />
                        ) : (
                          <ChevronDown
                            className="flex-shrink-0 text-primary"
                            size={20}
                          />
                        )}
                      </div>

                      {openFAQ === faq.id && (
                        <div className="mt-4 border-t border-base-300 pt-4">
                          <p className="leading-relaxed text-base-content/80">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Still Need Help */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold">Still Need Help?</h2>
              <p className="text-xl opacity-90">
                Our support team is here to help you 24/7
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="card bg-primary-content text-primary">
                <div className="card-body text-center">
                  <Phone className="mx-auto mb-4" size={32} />
                  <h3 className="card-title justify-center">Call Us</h3>
                  <a
                    className="mb-4 text-primary/70 hover:underline"
                    href={`tel:${(contactInfo?.phone || "0800-HAREENG").replace(/\s/g, "")}`}
                  >
                    {contactInfo?.phone || "0800-HAREENG"}
                  </a>
                  <p className="text-sm">Available 24/7 for urgent issues</p>
                </div>
              </div>

              <div className="card bg-primary-content text-primary">
                <div className="card-body text-center">
                  <MessageSquare className="mx-auto mb-4" size={32} />
                  <h3 className="card-title justify-center">Live Chat</h3>
                  <p className="mb-4 text-primary/70">Chat with support</p>
                  <p className="text-sm">Get instant help through our app</p>
                </div>
              </div>

              <div className="card bg-primary-content text-primary">
                <div className="card-body text-center">
                  <Mail className="mx-auto mb-4" size={32} />
                  <h3 className="card-title justify-center">Email Us</h3>
                  <a
                    className="mb-4 text-primary/70 hover:underline"
                    href={`mailto:${contactInfo?.email || "support@hareeng.org"}`}
                  >
                    {contactInfo?.email || "support@hareeng.org"}
                  </a>
                  <p className="text-sm">We will respond within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <a className="btn btn-primary btn-lg" href="/support/emergency-report">
                  Contact Support
                </a>
                <a className="btn btn-outline btn-lg" href="/support/report-issue">
                  Report an Issue
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* Quick Links */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="mb-12 text-center text-3xl font-bold">
              Quick Links
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <a
                className="card bg-base-200 transition-shadow hover:shadow-lg"
                href="/driver/vehicle-driver-signup"
              >
                <div className="card-body text-center">
                  <Car className="mx-auto mb-2 text-primary" size={24} />
                  <h3 className="card-title justify-center text-lg">
                    Become Taxi Driver
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Start earning with your car
                  </p>
                </div>
              </a>

              <a
                className="card bg-base-200 transition-shadow hover:shadow-lg"
                href="/driver/delivery-signup"
              >
                <div className="card-body text-center">
                  <Package className="mx-auto mb-2 text-primary" size={24} />
                  <h3 className="card-title justify-center text-lg">
                    Delivery Driver
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Deliver food and packages
                  </p>
                </div>
              </a>

              <a
                className="card bg-base-200 transition-shadow hover:shadow-lg"
                href="/safety/rider-safety"
              >
                <div className="card-body text-center">
                  <Shield className="mx-auto mb-2 text-primary" size={24} />
                  <h3 className="card-title justify-center text-lg">
                    Safety Center
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Learn about our safety features
                  </p>
                </div>
              </a>

              <a
                className="card bg-base-200 transition-shadow hover:shadow-lg"
                href="/pages/about-us/cities-we-serve"
              >
                <div className="card-body text-center">
                  <MapPin className="mx-auto mb-2 text-primary" size={24} />
                  <h3 className="card-title justify-center text-lg">
                    Supported Cities
                  </h3>
                  <p className="text-sm text-base-content/70">
                    See where we operate
                  </p>
                </div>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
